import { getLocalStorageItem, setLocalStorageItem } from './browserStorage';

export const CONSENT_STORAGE_KEY = 'ga_consent_status';
export const OPEN_CONSENT_BANNER_EVENT = 'mcda:open-consent-banner';

const GA_SCRIPT_ID = 'mcda-ga-script';
const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_gcl'];

function getWindow() {
  return typeof window === 'undefined' ? null : window;
}

function getDocument() {
  return typeof document === 'undefined' ? null : document;
}

export function getAnalyticsConsentStatus() {
  const value = getLocalStorageItem(CONSENT_STORAGE_KEY);
  return value === 'granted' || value === 'denied' ? value : null;
}

export function setAnalyticsConsentStatus(status) {
  if (status !== 'granted' && status !== 'denied') {
    return false;
  }

  return setLocalStorageItem(CONSENT_STORAGE_KEY, status);
}

export function getGaMeasurementId() {
  const currentWindow = getWindow();
  const currentDocument = getDocument();
  const measurementId =
    currentWindow?.MCDA_GA_MEASUREMENT_ID ||
    currentDocument
      ?.querySelector('meta[name="mcda-ga-measurement-id"]')
      ?.getAttribute('content');

  return typeof measurementId === 'string' ? measurementId.trim() : '';
}

export function isAnalyticsConfigured() {
  return getGaMeasurementId().length > 0;
}

function ensureGtag() {
  const currentWindow = getWindow();
  if (!currentWindow) {
    return null;
  }

  currentWindow.dataLayer = currentWindow.dataLayer || [];

  if (typeof currentWindow.gtag !== 'function') {
    currentWindow.gtag = function gtag() {
      currentWindow.dataLayer.push(arguments);
    };
  }

  return currentWindow.gtag;
}

function setDefaultConsent() {
  const currentWindow = getWindow();
  const gtag = ensureGtag();

  if (!currentWindow || !gtag || currentWindow.MCDA_GA_DEFAULT_CONSENT_SET) {
    return;
  }

  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  currentWindow.MCDA_GA_DEFAULT_CONSENT_SET = true;
}

export function updateGoogleConsent(granted) {
  const gtag = ensureGtag();
  if (!gtag) {
    return;
  }

  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

function getCookieDeletionDomains(hostname) {
  if (!hostname || hostname === 'localhost' || /^[\d.]+$/.test(hostname)) {
    return [null];
  }

  const parts = hostname.split('.');
  const domains = new Set([hostname, `.${hostname}`]);

  for (let index = 1; index < parts.length - 1; index += 1) {
    domains.add(`.${parts.slice(index).join('.')}`);
  }

  return [null, ...domains];
}

export function deleteAnalyticsCookies() {
  const currentDocument = getDocument();
  if (!currentDocument?.cookie) {
    return;
  }

  const cookieNames = currentDocument.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) =>
      ANALYTICS_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))
    );

  const domains = getCookieDeletionDomains(currentDocument.location.hostname);
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0';

  cookieNames.forEach((name) => {
    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : '';
      currentDocument.cookie = `${name}=; ${expiry}; path=/${domainPart}; SameSite=Lax`;
    });
  });
}

function injectGoogleAnalyticsScript(measurementId) {
  const currentDocument = getDocument();
  if (!currentDocument || currentDocument.getElementById(GA_SCRIPT_ID)) {
    return;
  }

  const script = currentDocument.createElement('script');
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId
  )}`;
  currentDocument.head.appendChild(script);
}

export function loadGoogleAnalytics() {
  const measurementId = getGaMeasurementId();
  const currentWindow = getWindow();

  if (!measurementId || !currentWindow) {
    return false;
  }

  setDefaultConsent();
  updateGoogleConsent(true);
  injectGoogleAnalyticsScript(measurementId);

  if (!currentWindow.MCDA_GA_CONFIGURED) {
    const gtag = ensureGtag();
    gtag('js', new Date());
    gtag('config', measurementId);
    currentWindow.MCDA_GA_CONFIGURED = true;
  }

  return true;
}

export function denyGoogleAnalytics() {
  setDefaultConsent();
  updateGoogleConsent(false);
  deleteAnalyticsCookies();
}
